<template>
  <div data-testid="staff-subscription-plans-table">
    <data-table
      ref="datatableRef"
      test-id-prefix="staff-subscription-plans"
      :config="tableConfig"
      :data="displayGroups"
      display-mode="table"
      :refreshButton="{ show: true }"
      @button-click="handleButtonClick"
      @refresh="handleRefresh"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { useSubscriptionAdmin } from '../../../composables/useSubscriptionAdmin'
import { MICRO_PER_CREDIT } from '../../../composables/useCreditWallet'
import { log } from '../../../utils/DebugLogger'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import type { PlanGroup } from '../../../composables/useSubscriptionAdmin'

const props = withDefaults(defineProps<{
  /** D3: when true the list includes retired (active=0) plans. */
  showInactive?: boolean
}>(), {
  showInactive: false,
})

const { t, n } = useI18n()
const { groupedPlans, listPlans, setPlanActive } = useSubscriptionAdmin()

interface DataTableExposed {
  loadData: () => void
}

const datatableRef = ref<DataTableExposed | null>(null)

// Locale map used only to derive the minor-unit factor for a given currency.
const CURRENCY_LOCALE: Record<string, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  CLP: 'es-CL',
}

/**
 * Build the Mercados summary string for a plan group.
 * Returns '—' (em dash) when the plan has zero markets.
 * Each market is formatted via n() with its own currency.
 * Markets joined with ' · ' (BRL first, then alphabetical — per groupedPlans sort).
 */
function marketsDisplay(group: PlanGroup): string {
  if (group.markets.length === 0) return '—'
  return group.markets
    .map((m) => {
      const locale = CURRENCY_LOCALE[m.currency] ?? 'pt-BR'
      const factor = currencyFactor(locale, m.currency)
      const majorValue = m.price_cents / factor
      return n(majorValue, { style: 'currency', currency: m.currency, currencyDisplay: 'symbol' })
    })
    .join(' · ')
}

// Derived rows: one row per plan group, with a computed mercados_display string.
// monthly_credit_grant is stored in MICRO-credits (like ai_credit_packs.credits);
// the "Créditos mensais" column shows CREDITS, the same unit the plan form collects.
const displayGroups = computed(() =>
  groupedPlans.value.map((group: PlanGroup) => ({
    ...group,
    monthly_credit_grant_display: group.monthly_credit_grant / MICRO_PER_CREDIT,
    mercados_display: marketsDisplay(group),
  })),
)

const tableConfig = computed(() => ({
  tableId: 'tabelaSubscriptionPlans',
  title: t('Planos de assinatura'),
  columns: [
    {
      data: 'name',
      title: t('Nome'),
      width: 200,
      sortable: true,
    },
    {
      data: 'monthly_credit_grant_display',
      title: t('Créditos mensais'),
      width: 140,
      sortable: true,
      renderType: 'integer',
    },
    {
      data: 'cycle',
      title: t('Ciclo'),
      width: 100,
      sortable: true,
    },
    {
      data: 'active',
      title: t('Ativo'),
      width: 80,
      sortable: true,
      renderType: 'active_inactive',
    },
    {
      data: 'mercados_display',
      title: t('Mercados'),
      width: 250,
      sortable: false,
    },
    {
      data: 'id',
      title: t('Editar'),
      width: 80,
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Editar'),
      buttonIcon: 'pi pi-pencil',
      buttonAction: 'edit',
      buttonTestId: (row: Record<string, unknown>) =>
        `staff-subscription-plan-edit-row-${row.id as number}`,
    },
    {
      data: 'active',
      title: t('Ativar/Desativar'),
      width: 130,
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Ativar/Desativar'),
      buttonIcon: 'pi pi-power-off',
      buttonAction: 'toggle-active',
      buttonTestId: (row: Record<string, unknown>) =>
        `staff-subscription-plan-toggle-row-${row.id as number}`,
    },
  ],
  footer: false,
  rowTestId: (row: Record<string, unknown>) => {
    const id = row?.id
    return id == null ? '' : `staff-subscription-plan-row-${id as number | string}`
  },
}))

async function loadData(): Promise<void> {
  try {
    await listPlans({ includeInactive: props.showInactive })
  } catch (err) {
    log('StaffSubscriptionTable', 'loadData', 'Erro ao carregar planos', err)
  }
}

async function handleToggleActive(row: PlanGroup): Promise<void> {
  try {
    const newActive = row.active === 1 ? 0 : 1
    await setPlanActive(row.id, newActive as 0 | 1)
    eventBus.emit('globalMessage.success', t('Plano atualizado'))
    await loadData()
  } catch (err) {
    log('StaffSubscriptionTable', 'handleToggleActive', 'Erro ao atualizar plano', err)
    eventBus.emit('globalMessage.error', t('Erro ao atualizar plano'))
  }
}

function handleButtonClick(e: { value: unknown; data: PlanGroup & { mercados_display: string }; action: string }): void {
  if (e.action === 'edit') {
    // includeInactive: the modal re-lists after a save and cannot read this
    // prop (it is registered globally in ModalManager), so the breadth of the
    // list being edited travels with the open.
    eventBus.emit('modal.staffSubscriptionPlan.open', { plan: e.data, includeInactive: props.showInactive })
  } else if (e.action === 'toggle-active') {
    void handleToggleActive(e.data)
  }
}

function handleRefresh(): void {
  void loadData()
}

// D3: flipping the page's "Mostrar inativos" toggle re-lists with the flag.
watch(() => props.showInactive, () => {
  void loadData()
})

onMounted(() => {
  eventBus.on('staffSubscription.refresh', handleRefresh)
})

onBeforeUnmount(() => {
  eventBus.off('staffSubscription.refresh', handleRefresh)
})

defineExpose({ loadData })
</script>
